import { Injectable, signal } from '@angular/core';

import { BetContext, BetContextInfo, BetContextResponse } from '@bpos/v2/bet-context';
import { ApiService } from '@frontend/sports/common/api-utils';
import { LoggerFactory, SportsRemoteLogger } from '@frontend/sports/common/core/feature/logging';
import { isArray } from 'lodash';
import { catchError, map, of, tap } from 'rxjs';

import { BetContextualInfoV2 } from '../../../../model/bet-context';

@Injectable({
    providedIn: 'root',
})
export class BetContextualDataService {
    private readonly logger: SportsRemoteLogger;
    private betContextualInfoMap = new Map<string, BetContextualInfoV2[]>();
    private betContextualInfo = signal<Map<string, BetContextualInfoV2[]>>(this.betContextualInfoMap);

    constructor(
        private apiService: ApiService,
        loggerFactory: LoggerFactory,
    ) {
        this.logger = loggerFactory.getLogger('BetContextualDataService');
    }

    getBetContextualSavedData(betslipIds: string[]): void {
        const newBetslipIds = this.getNewBetslipIds(betslipIds);
        if (!newBetslipIds.length) {
            return;
        }
        this.apiService
            .get<BetContextResponse>('bet-context/get-tracking-data', { betslipIds: newBetslipIds })
            .pipe(
                map((response) => response?.betContexts ?? []),
                tap((bc) => this.mapBetContextualData(bc)),
                catchError((error) => {
                    this.logger.error(error, 'Unable to retrieve bet contextual data.');

                    return of([]);
                }),
            )
            .subscribe();
    }

    private getNewBetslipIds(betslipIds: string[]): string {
        return betslipIds.filter((betslipId) => !this.betContextualInfoMap.has(betslipId)).join(',');
    }

    private mapBetContextualData(betContexts: BetContextInfo[]): void {
        betContexts.forEach((betslipContext: BetContextInfo) => {
            this.betContextualInfoMap.set(betslipContext.betNumber, this.parseBetContextualData(betslipContext.betContexts?.[0]));
        });
        this.betContextualInfo.set(this.betContextualInfoMap);
    }

    private parseBetContextualData(betslipContext: BetContext): BetContextualInfoV2[] {
        let betslipContextData: BetContextualInfoV2[] = [];
        if (betslipContext?.data) {
            try {
                betslipContextData = JSON.parse(betslipContext.data);
                betslipContextData = isArray(betslipContextData) ? betslipContextData : [betslipContextData];
                if (!(betslipContextData.length > 0 && 'marketType' in betslipContextData[0])) {
                    betslipContextData = [];
                }
            } catch (e) {
                this.logger.error(e, 'Error occurred while parsing BetContextual data');
            }
        }
        return betslipContextData;
    }

    get betContextualDataSignal() {
        return this.betContextualInfo;
    }
}
